/**
 * The two places a counterparty's contact email can come from: the entity
 * (`Payer` / `Payee`) and its search row (`PayerRow` / `PayeeRow`). Structural
 * so all four models fit without a union.
 */
export interface CounterpartyEmailInput {
  counterparty?: { email?: string | null };
  row?: { email?: string | null };
}

// The search index resolves a counterparty with no reachable contact email to
// `wsv3-<accountId>@noreply.wingspan.app` — a marker, not a mailbox.
const isPlaceholderAddress = (email: string) =>
  email.toLowerCase().endsWith("@noreply.wingspan.app");

/**
 * Resolves a counterparty's contact email, entity first and search row second.
 *
 * `Payer.email` is populated only when the relationship's payee half recorded
 * the address, so it is absent for a linked client (WIN-15906) — which is what
 * rendered "Sent to undefined" in the invoice drawer (PLA-3667). The row's
 * `email` is the server-resolved address and covers that case (PLA-3646).
 *
 * Do NOT substitute `InvoiceRow.email`: it is the same field on a different
 * projection and it is not the counterparty's address. Where the row's `profile`
 * fails to resolve it holds the *viewer's own* email, which looks entirely
 * plausible in a "Sent to …" line and is the wrong party.
 */
export const selectorCounterpartyEmail = ({
  counterparty,
  row
}: CounterpartyEmailInput): string | undefined =>
  [counterparty?.email, row?.email]
    .map(email => email?.trim())
    .find((email): email is string => !!email && !isPlaceholderAddress(email));
